import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/t64300ho_SH%20Logo.png";

const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'home', label: t('nav.home') },
    { id: 'about', label: t('nav.about') },
    { id: 'specialities', label: t('nav.specialities') },
    { id: 'facilities', label: t('nav.facilities') },
    { id: 'testimonials', label: t('nav.testimonials') },
    { id: 'contact', label: t('nav.contact') },
  ];

  return (
    <>
      {/* Main Navigation */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white shadow-lg py-2' 
            : 'bg-white/95 backdrop-blur-md py-3'
        }`}
        data-testid="main-navbar"
      >
        <div className="section-container">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => scrollToSection('home')}
              className="flex items-center"
              data-testid="logo-button"
            >
              <img 
                src={LOGO_URL} 
                alt="Sakthi Hospital Logo" 
                className={`transition-all duration-300 ${isScrolled ? 'h-12' : 'h-14'} w-auto object-contain`}
              />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="px-4 py-2 text-[#1a2b3c] hover:text-[#0066b3] font-medium text-sm transition-colors duration-200 rounded-lg hover:bg-[#0066b3]/5"
                  data-testid={`nav-link-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden" data-testid="language-toggle">
                <button
                  onClick={() => language !== 'en' && toggleLanguage()}
                  className={`px-3 py-2 text-sm font-medium transition-all ${
                    language === 'en' 
                      ? 'bg-[#0066b3] text-white' 
                      : 'text-gray-600 hover:text-[#0066b3]'
                  }`}
                  data-testid="lang-en"
                >
                  EN
                </button>
                <button
                  onClick={() => language !== 'ta' && toggleLanguage()}
                  className={`px-3 py-2 text-sm font-medium transition-all ${
                    language === 'ta' 
                      ? 'bg-[#0066b3] text-white' 
                      : 'text-gray-600 hover:text-[#0066b3]'
                  }`}
                  data-testid="lang-ta"
                >
                  தமிழ்
                </button>
              </div>

              {/* Emergency Number - Desktop */}
              <a
                href="tel:+914428461515"
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#c41e3a]/10 text-[#c41e3a] rounded-lg font-medium text-sm hover:bg-[#c41e3a]/20 transition-colors"
              >
                <Phone size={16} className="animate-pulse" />
                <span>Emergency</span>
              </a>

              {/* Book Appointment Button - Desktop */}
              <button
                onClick={() => scrollToSection('appointment')}
                className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-[#0066b3] text-white rounded-lg font-semibold text-sm hover:bg-[#004a82] transition-colors shadow-md shadow-[#0066b3]/20"
                data-testid="nav-book-appointment-btn"
              >
                {t('nav.bookAppointment')}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-2 text-[#1a2b3c] hover:bg-gray-100 rounded-lg"
                data-testid="mobile-menu-toggle"
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer for fixed navbar */}
      <div className={`${isScrolled ? 'h-16' : 'h-20'}`}></div>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 z-[100] bg-white transform transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`} 
        data-testid="mobile-menu"
      >
        <div className="section-container py-4">
          <div className="flex items-center justify-between mb-8">
            <img 
              src={LOGO_URL} 
              alt="Sakthi Hospital Logo" 
              className="h-12 w-auto object-contain"
            />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-[#1a2b3c] hover:bg-gray-100 rounded-lg"
              data-testid="mobile-menu-close"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-left py-3 px-4 text-lg text-[#1a2b3c] hover:bg-[#0066b3]/5 rounded-xl transition-colors font-medium"
                data-testid={`mobile-nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="mt-8 space-y-3">
            <a
              href="tel:+914428461515"
              className="flex items-center justify-center gap-2 w-full py-4 bg-[#c41e3a] text-white rounded-xl font-semibold"
            >
              <Phone size={20} />
              Emergency: +91 44 2846 1515
            </a>
            <button
              onClick={() => scrollToSection('appointment')}
              className="w-full py-4 bg-[#0066b3] text-white rounded-xl font-semibold"
              data-testid="mobile-book-appointment-btn"
            >
              {t('nav.bookAppointment')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
